import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { API_URL } from '../utils/api';

const Input = styled('input')({
  display: 'none',
});

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  margin: '0 auto',
  border: '4px solid var(--gradient-end)',
  boxShadow: 'var(--elevation-2)',
  background: 'linear-gradient(90deg, rgba(255,106,171,0.06), rgba(123,97,255,0.06))',
}));

const LocationButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5),
  display: 'flex',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  textTransform: 'none',
  border: '1px solid rgba(123,97,255,0.08)',
  borderRadius: 'var(--radius-sm)',
  '&:hover': {
    backgroundColor: 'rgba(123,97,255,0.03)',
  },
}));

const EditProfileDialog = ({ open, onClose, userData, onSave }) => {
  const parseInitialLocation = (locationData) => {
    if (!locationData) return null;
    if (typeof locationData === 'object' && locationData.type === 'Point') {
      return locationData;
    }
    if (typeof locationData === 'string') {
      const [lat, lon] = locationData.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lon)) {
        return {
          type: 'Point',
          coordinates: [lon, lat]
        };
      }
    }
    return null;
  };

  const [formData, setFormData] = useState({
    name: userData.name || '',
    location: parseInitialLocation(userData.locationData || userData.location) || null,
    locationDetails: '',
    birthday: userData.birthday || '',
    bio: userData.bio || '',
    avatar: userData.avatar || null,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [locationStatus, setLocationStatus] = useState('');
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        name: userData.name || '',
        location: parseInitialLocation(userData.locationData || userData.location) || null,
        locationDetails: '',
        birthday: userData.birthday || '',
        bio: userData.bio || '',
        avatar: userData.avatar || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userData]);

  const getLocationDetails = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
      );
      const data = await response.json();
      return {
        displayName: data.display_name,
        area: data.address.suburb || data.address.town || data.address.city || '',
        city: data.address.city || data.address.town || '',
        state: data.address.state || '',
        country: data.address.country || ''
      };
    } catch (error) {
      console.error('Error getting location details:', error);
      return null;
    }
  };

  const handleLocationClick = async () => {
    setIsLocating(true);
    setLocationStatus('Getting your location...');

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newLocation = {
            type: 'Point',
            coordinates: [position.coords.longitude, position.coords.latitude]
          };

          // Get location details
          const details = await getLocationDetails(position.coords.latitude, position.coords.longitude);
          
          setFormData(prev => ({
            ...prev,
            location: newLocation,
            locationDetails: details ? details : null
          }));
          
          setLocationStatus('Location updated successfully!');
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationStatus('Could not get your location. Please try again.');
          setIsLocating(false);
        }
      );
    } else {
      setLocationStatus('Geolocation is not supported by your browser.');
      setIsLocating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('photo', file);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token missing. Please try logging in again.');
      }

      const uploadResponse = await axios.post(`${API_URL}/api/users/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      if (uploadResponse.data.url) {
        setFormData(prev => ({
          ...prev,
          avatar: uploadResponse.data.url
        }));
      } else {
        throw new Error('No photo URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(`Server error: ${error.response.status} - ${error.response.data.message || error.response.statusText}`);
      } else if (error.request) {
        // The request was made but no response was received
        setError('Network error: No response from server. Check your internet connection.');
      } else {
        // Something happened in setting up the request
        setError(`Error: ${error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    onSave(formData);
    onClose();
  };

  const getLocationStatusColor = () => {
    if (!locationStatus) return 'inherit';
    if (locationStatus.includes('successfully')) return 'success.main';
    if (locationStatus.includes('Getting')) return 'info.main';
    return 'error.main';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Your Profile</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2, textAlign: 'center', position: 'relative' }}>
          <LargeAvatar src={formData.avatar} alt={formData.name}>
            {!formData.avatar && formData.name?.charAt(0)}
          </LargeAvatar>
          
          <label htmlFor="icon-button-file">
            <Input
              accept="image/*"
              id="icon-button-file"
              type="file"
              onChange={handleImageChange}
              disabled={uploading}
            />
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="span"
              sx={{ mt: 1 }}
              disabled={uploading}
            >
              {uploading ? <CircularProgress size={24} /> : <PhotoCamera />}
            </IconButton>
          </label>
        </Box>
        
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          margin="normal"
          required
          error={!formData.name.trim()}
          helperText={!formData.name.trim() ? 'Name is required' : ''}
        />
        
        <Tooltip title="Click to update your location" placement="top">
          <LocationButton
            onClick={handleLocationClick}
            disabled={isLocating}
            variant="outlined"
            color={locationStatus.includes('successfully') ? 'success' : 'primary'}
            startIcon={isLocating ? <CircularProgress size={20} /> : <LocationOnIcon />}
            endIcon={locationStatus.includes('successfully') && <CheckCircleIcon />}
          >
            <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
              <Typography variant="body1" component="div">
                {isLocating ? 'Getting location...' : 'Update Location'}
              </Typography>
              {formData.location && formData.location.coordinates && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`${formData.location.coordinates[1].toFixed(6)}, ${formData.location.coordinates[0].toFixed(6)}`}
                  </Typography>
                  {formData.locationDetails && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {formData.locationDetails.area && `${formData.locationDetails.area}, `}
                      {formData.locationDetails.city && `${formData.locationDetails.city}, `}
                      {formData.locationDetails.state && `${formData.locationDetails.state}, `}
                      {formData.locationDetails.country}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </LocationButton>
        </Tooltip>
        {locationStatus && (
          <Typography 
            variant="caption" 
            sx={{ 
              ml: 1, 
              color: getLocationStatusColor(),
              display: 'block',
              mb: 1 
            }}
          >
            {locationStatus}
          </Typography>
        )}
        
        <TextField
          fullWidth
          label="Birthday"
          name="birthday"
          type="date"
          value={formData.birthday}
          onChange={handleChange}
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
        />
        
        <TextField
          fullWidth
          label="Bio"
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          margin="normal"
          multiline
          rows={4}
          placeholder="Tell us about yourself..."
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: 'var(--radius-sm)' }}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={uploading || !formData.name.trim()}
          sx={{
            background: 'linear-gradient(90deg,var(--gradient-start),var(--gradient-end))',
            boxShadow: 'var(--elevation-2)',
            color: 'white',
            textTransform: 'none',
            borderRadius: 'var(--radius-sm)'
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfileDialog; 